import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from './useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from './use-toast';

export interface TaskList {
  id: string;
  family_id: string;
  name: string;
  icon: string;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface TaskPreview {
  id: string;
  title: string;
  due_date: string | null;
  assigned_profile_ids: string[];
  assignees: {
    profile_id: string;
    displayName: string;
    avatar_url: string | null;
    initials: string;
  }[];
}

export interface TaskListWithStats extends TaskList {
  pending_count: number;
  completed_count: number;
  member_avatars: string[];
  task_previews: TaskPreview[];
  event_id?: string;
  description?: string;
}

const CACHE_TIME = 10 * 60 * 1000; // 10 minutes
const STALE_TIME = 60 * 1000; // 1 minute

export function useTaskLists(familyId?: string) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Query for task lists with stats (use same key as StartupLoader)
  const taskListsQuery = useQuery({
    queryKey: ['task-lists-with-stats', familyId],
    queryFn: async (): Promise<TaskListWithStats[]> => {
      if (!familyId) return [];

      try {
        // First get task lists - order by display_order, then created_at as fallback
        const { data: taskListsData, error: listsError } = await supabase
          .from('task_lists')
          .select('*')
          .eq('family_id', familyId)
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (listsError) throw listsError;

        // Then get tasks for each list with more details for preview
        const { data: tasksData, error: tasksError } = await supabase
          .from('family_tasks')
          .select('id, list_id, title, due_date, assigned_to, assigned_to_list, status, display_order')
          .eq('family_id', familyId)
          .order('display_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (tasksError) {
          console.warn('Error fetching tasks:', tasksError);
        }

        // Get current user profile
        const { data: currentProfile, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_id', user?.id)
          .single();

        if (profileError) {
          console.warn('Error fetching current profile:', profileError);
        }

        // Get accessible task list IDs for the current user
        const accessibleListIds = new Set<string>();
        
        // Add lists created by the user
        if (currentProfile?.id) {
          (taskListsData || []).forEach(list => {
            if (list.created_by === currentProfile.id) {
              accessibleListIds.add(list.id);
            }
          });
        }
        
        // Add lists where user is a member
        if (currentProfile?.id) {
          const { data: memberListsData, error: memberListsError } = await supabase
            .from('task_list_members')
            .select('task_list_id')
            .eq('profile_id', currentProfile.id);

          if (!memberListsError && memberListsData) {
            memberListsData.forEach(member => accessibleListIds.add(member.task_list_id));
          }
        }

        console.log('Current profile ID:', currentProfile?.id);
        console.log('All task lists:', (taskListsData || []).map(l => ({ id: l.id, name: l.name, created_by: l.created_by })));
        console.log('Accessible list IDs:', Array.from(accessibleListIds));

        // Find orphaned tasks (assigned to user but list not accessible)
        const orphanedTasks = (tasksData || []).filter(task => {
          if (!currentProfile?.id || !task.list_id) return false;
          
          const isAssigned = task.assigned_to_list?.includes(currentProfile.id) || task.assigned_to === currentProfile.id;
          const hasInaccessibleList = !accessibleListIds.has(task.list_id);
          
          return isAssigned && hasInaccessibleList;
        });
        
        // Get list members with profiles separately to avoid relation issues
        const listIds = (taskListsData || []).map(list => list.id);
        const { data: membersData, error: membersError } = await supabase
          .from('task_list_members')
          .select('task_list_id, profile_id')
          .in('task_list_id', listIds);

        if (membersError) {
          console.warn('Error fetching list members:', membersError);
        }

        // Get profile data for members and task assignees
        const memberProfileIds = (membersData || []).map(m => m.profile_id);
        // Collect all assignee ids from tasks (assigned_to and assigned_to_list)
        const allAssigneeIds = Array.from(new Set((tasksData || []).flatMap(t => {
          const ids: string[] = [];
          if ((t as any).assigned_to) ids.push((t as any).assigned_to);
          if ((t as any).assigned_to_list && Array.isArray((t as any).assigned_to_list)) ids.push(...(t as any).assigned_to_list);
          return ids;
        })));
        
        const { data: profilesData, error: profilesError } = await supabase
          .from('profiles')
          .select('id, avatar_url, first_name, last_name')
          .in('id', Array.from(new Set([...memberProfileIds, ...allAssigneeIds])));

        if (profilesError) {
          console.warn('Error fetching profiles:', profilesError);
        }
        
        // Fetch person profiles for any assignee ids that are not profiles
        const profileIdsSet = new Set((profilesData || []).map(p => p.id));
        const remainingIds = allAssigneeIds.filter(id => !profileIdsSet.has(id));
        const { data: personProfilesData, error: personError } = remainingIds.length > 0 ? await supabase
          .from('person_profiles')
          .select('id, first_name, last_name, avatar_url, profile_id')
          .in('id', remainingIds) : { data: [], error: null };

        if (personError) {
          console.warn('Error fetching person profiles:', personError);
        }
        
        // Get family members for child avatars and names
        const { data: familyMembersData, error: familyError } = await supabase
          .from('family_members')
          .select('profile_id, child_name, child_avatar_url')
          .eq('family_id', familyId);

        if (familyError) {
          console.warn('Error fetching family members:', familyError);
        }

        // Transform data to include stats, member avatars and task previews
        const transformedLists = (taskListsData || []).map(list => {
          try {
            const tasks = (tasksData || []).filter(t => t.list_id === list.id);
            const pending_count = tasks.filter(t => t.status === 'pending').length;
            const completed_count = tasks.filter(t => t.status === 'completed').length;
            
            const listMembers = (membersData || []).filter(m => m.task_list_id === list.id);
            // Keep array length equal to members count so UI can show fallbacks when avatar_url is missing
            const member_avatars = listMembers.map(member => {
              const profile = (profilesData || []).find(p => p.id === member.profile_id);
              return profile?.avatar_url || '';
            });

            // Get preview of first 3 pending tasks - ALWAYS ensure this is an array
            const pendingTasks = tasks.filter(t => t.status === 'pending').slice(0, 3);
            const task_previews: TaskPreview[] = pendingTasks.map(task => {
              try {
                const raw_ids: string[] = ((task as any).assigned_to_list && (task as any).assigned_to_list.length > 0)
                  ? (task as any).assigned_to_list
                  : (task.assigned_to ? [task.assigned_to] : []);

                // Deduplicate
                const assigned_profile_ids = Array.from(new Set(raw_ids));

                // Build assignees supporting both profiles and person_profiles
                const assignees = assigned_profile_ids.flatMap((pid) => {
                  try {
                    // If it's a real profile id
                    const profile = (profilesData || []).find(p => p.id === pid);
                    if (profile) {
                      const assigneeFamilyMember = (familyMembersData || []).find(fm => fm.profile_id === pid);
                      const name = assigneeFamilyMember?.child_name || 
                        ((profile?.first_name && profile?.last_name)
                          ? `${profile.first_name} ${profile.last_name}`
                          : (profile?.first_name || 'Membre inconnu'));
                      const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
                      const avatar_url = profile?.avatar_url || assigneeFamilyMember?.child_avatar_url || null;
                      return [{ profile_id: pid, displayName: name, avatar_url, initials }];
                    }
                    // Otherwise try person_profile by id
                    const person = (personProfilesData || []).find(pp => pp.id === pid);
                    if (person) {
                      // If this person links to a profile that is already included, skip to avoid duplicates
                      const linkedPid = (person as any).profile_id;
                      if (linkedPid && assigned_profile_ids.includes(linkedPid)) {
                        return [] as any[];
                      }
                      const name = `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim() || 'Contact';
                      const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
                      return [{ profile_id: pid, displayName: name, avatar_url: person.avatar_url || null, initials }];
                    }
                    // Unknown id
                    const name = 'Membre inconnu';
                    const initials = 'MI';
                    return [{ profile_id: pid, displayName: name, avatar_url: null, initials }];
                  } catch (error) {
                    console.warn('Error processing assignee:', error, pid);
                    return [];
                  }
                });

                return {
                  id: task.id,
                  title: task.title,
                  due_date: task.due_date,
                  assigned_profile_ids,
                  assignees
                };
              } catch (error) {
                console.warn('Error processing task preview:', error, task);
                // Return a basic task preview if there's an error
                return {
                  id: task.id,
                  title: task.title,
                  due_date: task.due_date,
                  assigned_profile_ids: [],
                  assignees: []
                };
              }
            });

            return {
              id: list.id,
              family_id: list.family_id,
              name: list.name,
              icon: list.icon,
              created_by: list.created_by,
              created_at: list.created_at,
              updated_at: list.updated_at,
              pending_count,
              completed_count,
              member_avatars,
              task_previews: task_previews || [], // ENSURE this is always an array
              event_id: (list as any).event_id || undefined
            };
          } catch (error) {
            console.warn('Error processing task list:', error, list);
            // Return basic list data if there's an error
            return {
              id: list.id,
              family_id: list.family_id,
              name: list.name,
              icon: list.icon,
              created_by: list.created_by,
              created_at: list.created_at,
              updated_at: list.updated_at,
              pending_count: 0,
              completed_count: 0,
              member_avatars: [],
              task_previews: [], // ALWAYS return an empty array
              event_id: (list as any).event_id || undefined
            };
          }
        });

        // Add virtual "General" list if there are orphaned shared tasks
        if (orphanedTasks.length > 0) {
          const pending_count = orphanedTasks.filter(t => t.status === 'pending').length;
          const completed_count = orphanedTasks.filter(t => t.status === 'completed').length;
          
          // Create task previews for virtual general tasks
          const pendingVirtualTasks = orphanedTasks.filter(t => t.status === 'pending').slice(0, 3);
          const virtual_task_previews: TaskPreview[] = pendingVirtualTasks.map(task => ({
            id: task.id,
            title: task.title,
            due_date: task.due_date,
            assigned_profile_ids: task.assigned_to_list || (task.assigned_to ? [task.assigned_to] : []),
            assignees: [] // We could populate this, but for now keep it simple
          }));

          const virtualGeneralList: TaskListWithStats = {
            id: 'virtual-general',
            family_id: familyId,
            name: 'Mes tâches reçues',
            description: 'Tâches partagées avec vous par d\'autres membres',
            icon: '📬',
            created_by: user?.id || '',
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            pending_count,
            completed_count,
            member_avatars: [],
            task_previews: virtual_task_previews,
            event_id: undefined
          };

          // Add the virtual list at the beginning (highest priority)
          return [virtualGeneralList, ...transformedLists];
        }

        return transformedLists;
      } catch (error) {
        console.error('Critical error in useTaskLists:', error);
        // Return empty array if there's a critical error
        return [];
      }
    },
    enabled: !!familyId,
    staleTime: 0, // Force fresh data after RLS fix
    gcTime: 0, // Clear cache immediately
    refetchOnWindowFocus: true,
    retry: 2, // Add retry for failed requests
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  // Mutation to create task list
  const createTaskListMutation = useMutation({
    mutationFn: async ({
      name,
      icon,
      memberIds,
      eventId
    }: {
      name: string;
      icon: string;
      memberIds: string[];
      eventId?: string;
    }) => {
      if (!familyId || !user) throw new Error('Missing familyId or user');

      // Get current user profile
      const { data: profile } = await supabase
        .from('profiles')
        .select('id')
        .eq('user_id', user.id)
        .single();

      if (!profile) throw new Error('Profile not found');

      // Create task list
      const { data: taskList, error: listError } = await supabase
        .from('task_lists')
        .insert([
          {
            family_id: familyId,
            name,
            icon,
            created_by: profile.id,
            event_id: eventId || null,
          }
        ])
        .select('*')
        .single();

      if (listError) throw listError;

      // Add members to the list
      if (memberIds.length > 0) {
        const { error: membersError } = await supabase
          .from('task_list_members')
          .insert(
            memberIds.map(profileId => ({
              task_list_id: taskList.id,
              profile_id: profileId
            }))
          );

        if (membersError) throw membersError;
      }

      return taskList;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({
        title: "Liste créée",
        description: "La nouvelle liste de tâches a été créée",
      });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de créer la liste",
        variant: "destructive",
      });
    },
  });

  // Mutation to delete task list
  const deleteTaskListMutation = useMutation({
    mutationFn: async (listId: string) => {
      const { error } = await supabase
        .from('task_lists')
        .delete()
        .eq('id', listId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({
        title: "Liste supprimée",
        description: "La liste de tâches a été supprimée",
      });
    },
  });

  // Mutation to add member to task list
  const addTaskListMemberMutation = useMutation({
    mutationFn: async ({ listId, profileId }: { listId: string; profileId: string }) => {
      const { error } = await supabase
        .from('task_list_members')
        .insert([{ task_list_id: listId, profile_id: profileId }]);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({
        title: "Membre ajouté",
        description: "Le membre a été ajouté à la liste",
      });
    },
  });

  // Mutation to remove member from task list
  const removeTaskListMemberMutation = useMutation({
    mutationFn: async ({ listId, profileId }: { listId: string; profileId: string }) => {
      const { error } = await supabase
        .from('task_list_members')
        .delete()
        .eq('task_list_id', listId)
        .eq('profile_id', profileId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({
        title: "Membre retiré",
        description: "Le membre a été retiré de la liste",
      });
    },
  });

  // Mutation to update task list
  const updateTaskListMutation = useMutation({
    mutationFn: async ({ listId, name, icon }: { listId: string; name: string; icon: string }) => {
      const { error } = await supabase
        .from('task_lists')
        .update({ name, icon, updated_at: new Date().toISOString() })
        .eq('id', listId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
      toast({
        title: "Liste mise à jour",
        description: "Les modifications ont été enregistrées",
      });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de modifier la liste",
        variant: "destructive",
      });
    },
  });

  // Mutation to update task lists order
  const updateTaskListsOrderMutation = useMutation({
    mutationFn: async (orderedListIds: string[]) => {
      const updatePromises = orderedListIds.map((listId, index) =>
        supabase
          .from('task_lists')
          .update({ display_order: index })
          .eq('id', listId)
      );

      const results = await Promise.all(updatePromises);
      const errors = results.filter(result => result.error);
      
      if (errors.length > 0) {
        throw new Error('Failed to update some task list orders');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['task-lists-with-stats', familyId] });
    },
    onError: () => {
      toast({
        title: "Erreur",
        description: "Impossible de réorganiser les listes",
        variant: "destructive",
      });
    },
  });

  return {
    taskLists: taskListsQuery.data || [],
    loading: taskListsQuery.isLoading,
    createTaskList: (name: string, icon: string = '📝', eventId?: string) =>
      createTaskListMutation.mutateAsync({ name, icon, memberIds: [], eventId }),
    deleteTaskList: deleteTaskListMutation.mutate,
    updateTaskList: (listId: string, name: string, icon: string) =>
      updateTaskListMutation.mutate({ listId, name, icon }),
    updateTaskListsOrder: (orderedListIds: string[]) =>
      updateTaskListsOrderMutation.mutate(orderedListIds),
    addTaskListMember: (listId: string, profileId: string) =>
      addTaskListMemberMutation.mutate({ listId, profileId }),
    removeTaskListMember: (listId: string, profileId: string) =>
      removeTaskListMemberMutation.mutate({ listId, profileId }),
    refreshTaskLists: () => taskListsQuery.refetch(),
  };
}